import React, { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue, useRecoilValueLoadable, useSetRecoilState } from 'recoil';
import * as AppState from '../../AppState';
import { LocationOrigin, Property } from '../../interfaces/interfaces';
import { googleMapsKey } from '@capcenter/shared';
import GoogleMapReact, { ChangeEventValue } from 'google-map-react';
import { MapMarker } from './MapMarkers/MapMarker';
import MapLoading from './MapLoading/MapLoading';
import { getPrice, setHoveredElementClassName, moveMapToLocation } from './MapUtilities';
import { getLocationFromSearch } from '../../utilities/locationUtils';
import { useNavigate } from 'react-router-dom';
import { useSetLocation } from '../../hooks/useLocation';
import useMapDrawing from '../../hooks/useMapDrawing';
import { useInitializeState } from '../../hooks/useInitializeState';

const Map = (props: { hidden: boolean }) => {
	const [gsMapTilesLoading, setGsMapTilesLoading] = useRecoilState(AppState.gsMapTilesLoading);
	const [gsUserLocation, setGsUserLocation] = useRecoilState(AppState.gsUserLocation);
	const [gsUserSearchLocation, setGsUserSearchLocation] = useRecoilState(AppState.gsUserSearchLocation);

	const setGsMapScriptLoaded = useSetRecoilState(AppState.gsMapScriptLoaded);

	const gsMapScriptLoaded = useRecoilValue(AppState.gsMapScriptLoaded);
	const gsIsMobile = useRecoilValue(AppState.gsIsMobile);
	const gsHoveredElement = useRecoilValue(AppState.gsHoveredElement);
	const gsProperties = useRecoilValueLoadable(AppState.gsProperties);
	const gsUserSearch = useRecoilValue(AppState.gsUserSearch);
	const gsFilters = useRecoilValue(AppState.gsFilters);
	const gsURL = useRecoilValue(AppState.gsURL);

	const gsMapOptions = useRecoilValue(AppState.gsMapOptions);

	const setLocation = useSetLocation();

	const [gmap, setGmap] = useState<google.maps.Map>();

	const navigate = useNavigate();

	const [MapDrawingControls, initializeMapDrawingManager] = useMapDrawing(gmap, gsIsMobile);

	useInitializeState();

	useEffect(() => {
		if (gsMapScriptLoaded && !gsMapTilesLoading) {
			navigate(gsURL);
		}
	}, [gsMapScriptLoaded, gsURL, gsMapTilesLoading, navigate]);

	/*
			this useEffect reacts to the user searches and functions that use the search (location from url, current location)
			and triggers the proper acton
	*/
	useEffect(() => {
		const handleUserSearch = async () => {

			//console.log('%cSearch with search: ', 'background: red;', gsUserSearch, gsUserSearchLocation, gsMapScriptLoaded);
			if (props.hidden) {
				/*
				while in mobile mode, map events don't fire, we have to manually set location
				*/
				if (Object.keys(gsUserSearch).length !== 0) {
					const newLocationFromSearch = await getLocationFromSearch(gsUserSearch);
					setGsUserLocation(newLocationFromSearch);
				} else if (gsUserSearchLocation && gsUserSearchLocation.origin === LocationOrigin.CurrentLocation) {
					setGsUserLocation(gsUserSearchLocation);
				}
			} else if (gsUserSearchLocation === null && Object.keys(gsUserSearch).length !== 0) {
				const newLocationFromSearch = await getLocationFromSearch(gsUserSearch);
				moveMapToLocation(gmap, newLocationFromSearch);
				setGsUserSearchLocation(newLocationFromSearch);
			} else if (gsUserSearchLocation) {
				moveMapToLocation(gmap, gsUserSearchLocation);
				// this handles when the search location is set but the location is so close to the default that the map doesn't move and generate 
				gmap?.panBy(1, 0);
			}
		};

		if (gsMapScriptLoaded)
			handleUserSearch();
	}, [gsMapScriptLoaded, gsUserSearch, gsUserSearchLocation, gmap, props.hidden, setGsUserLocation, setGsUserSearchLocation]);

	/*
	this useEffect handles any situations where the location might not have a centerMap to pass to the controlled map component
	i.e. the center property of GoogleMapReact component
	*/
	useEffect(() => {
		if (gsMapScriptLoaded && !gsUserLocation.centerMap && gsUserLocation.boundsElastic && gsUserLocation.zoomLevel) {
			moveMapToLocation(gmap, gsUserLocation);
		}
	}, [gsMapScriptLoaded, gsUserLocation, gmap]);

	useEffect(() => {
		setHoveredElementClassName(gsHoveredElement);
	}, [gsHoveredElement]);

	const onGoogleApiLoaded = (map: google.maps.Map) => {
		setGsMapScriptLoaded(true);
		setGmap(map);

		initializeMapDrawingManager(map);
	};

	const onMapChange = (ce: ChangeEventValue) => {
		setLocation(ce, gsUserSearchLocation);
	};

	return (
		<div className="home-search__map col position-relative" style={props?.hidden ? { display: 'none', zIndex: -1 } : { zIndex: 2 }}>
			<div id="google-map" className="bg-white h-100 w-100">
				<MapDrawingControls />
				<GoogleMapReact
					bootstrapURLKeys={{ key: googleMapsKey, libraries: ['places', 'drawing'] }}
					resetBoundsOnResize
					yesIWantToUseGoogleMapApiInternals
					zoom={gsUserLocation?.zoomLevel}
					center={gsUserLocation?.centerMap}
					onGoogleApiLoaded={({ map }) => onGoogleApiLoaded(map)}
					onChange={(changeEvent: ChangeEventValue) => onMapChange(changeEvent)}
					options={{ ...gsMapOptions, streetViewControl: gsIsMobile ? false : true }}
					onTilesLoaded={() => setGsMapTilesLoading(false)}
				>
					{(gsProperties.state === 'loading') ? (
						<MapLoading />
					) : (gsProperties.contents.propertyArray.map((property: Property) => {
						const price =
							gsFilters.propertyType === 'RecentlySold' && property.ClosePrice
								? property.ClosePrice
								: property.ListPrice;
						return (
							<MapMarker
								price={'$' + getPrice(Number(price))}
								key={`${property.StreetName}-${property.id}`}
								lat={property.location.lat}
								lng={property.location.lon}
								property={property}
							/>
						);
					}))}
				</GoogleMapReact>
			</div>
		</div>
	);

};

export default Map;